import React from 'react'
import isEmpty from 'lodash/isEmpty'
import { Card, Col, Container, Row } from 'react-bootstrap'

import type { InlineInputContentOptions } from '@ztechapps/webui.input'

import { blogPermissions, extractVideoUrls } from '@ztechapps/blog.common'
import { useRole } from '@ztechapps/common.hook'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import {
  RichTextDisplay,
  RichTextInlineInput,
  TagsInlineInput,
  TagsInput,
  TextAreaInlineInput,
  TextAreaInput,
} from '@ztechapps/webui.input'
import { DataSuspense, HorizontalStack } from '@ztechapps/webui.util'

import { BlogCategoryInlineInput } from '../BlogCategory/BlogCategoryInlineInput'
import { BlogStatusInlineInput } from '../BlogStatusInlineInput'
import { GenerateSummaryButton } from '../GenerateSummaryButton'
import { GenerateTagsButton } from '../GenerateTagsButton'
import { TranscribeVideosButton } from '../TranscribeVideosButton'
import { NO_EDIT_PERMISSION_REASON } from '../PermissionReasons'
import { assertSlugBeforeActive } from './assertSlugBeforeActive'
import { BlogInlineInput } from './BlogInlineInput'
import { useBlogItemContext } from './Context'

const ACTIVE_REQUIRES_SLUG_MESSAGE =
  'Add a slug before setting this blog to Active. The slug becomes its public address.'

export const Layout = () => {
  const { loading, document } = useBlogItemContext()
  const { hasRole: canEdit } = useRole({ roleName: blogPermissions.canEdit.value })

  const renderContent = (value: string | undefined, onClick: (() => void) | undefined, canEditContent: boolean) => (
    <div>
      <HorizontalStack gap={2} className="pb-4">
        <BusyButton
          text="Edit"
          onClick={onClick}
          icon="edit"
          disabledReason={canEditContent ? undefined : NO_EDIT_PERMISSION_REASON}
        />
        <TranscribeVideosButton
          blogId={document?._id ?? ''}
          hasVideos={!isEmpty(extractVideoUrls(value ?? ''))}
          transcribingMetadata={document?.transcribingMetadata}
          disabled={!document?._id || !canEditContent}
          disabledReason={canEditContent ? undefined : NO_EDIT_PERMISSION_REASON}
        />
      </HorizontalStack>

      <Container>
        {/* trustedSource: blog copy is authored by staff in this same admin app, not attacker-reachable */}
        <RichTextDisplay contentHtml={value} trustedSource />
      </Container>
    </div>
  )

  return (
    <DataSuspense loading={loading}>
      <Card>
        <Card.Body>
          <Row className="g-3">
            <Col md={12}>
              <BlogInlineInput name="title" displayContent={(e) => <h2>{e}</h2>} />
            </Col>
            <Col md={6}>
              <BlogInlineInput
                parentInputComponent={TextAreaInlineInput}
                name="summary"
                label="Summary"
                editContent={({
                  value,
                  setNewValue,
                  saveNow,
                  cancelNow,
                  submitting,
                  canEdit,
                }: InlineInputContentOptions) => (
                  <div>
                    <TextAreaInput
                      value={value ?? ''}
                      onChange={(e) => setNewValue(e.target.value)}
                      disabled={submitting}
                      rows={4}
                    />
                    <HorizontalStack gap={2} className="mt-2">
                      <GenerateSummaryButton
                        blogId={document?._id ?? ''}
                        onSummaryGenerated={(summary) => setNewValue(summary)}
                        disabled={!canEdit || submitting || !document?._id}
                      />
                      <SaveCancelButtons
                        isBusy={submitting}
                        disabled={!canEdit}
                        onSave={saveNow}
                        onCancel={cancelNow}
                      />
                    </HorizontalStack>
                  </div>
                )}
              />
            </Col>
            <Col md={6}>
              <BlogInlineInput
                parentInputComponent={BlogStatusInlineInput}
                name="status"
                label="Status"
                validate={(value) =>
                  assertSlugBeforeActive({
                    status: value?.value,
                    slug: document?.slug,
                    message: ACTIVE_REQUIRES_SLUG_MESSAGE,
                  })
                }
              />
            </Col>
            <Col md={6}>
              <BlogInlineInput
                parentInputComponent={BlogCategoryInlineInput}
                name="category"
                queryName="category { _id }"
                label="Category"
                disabledReason={NO_EDIT_PERMISSION_REASON}
              />
            </Col>
            <Col md={6}>
              <BlogInlineInput name="cover" label="Cover image url" />
            </Col>
            <Col md={6}>
              <BlogInlineInput
                name="slug"
                label="Slug"
                help="Becomes the blog's public address; must be unique."
                validate={(value) =>
                  assertSlugBeforeActive({
                    status: document?.status,
                    slug: value,
                    message: ACTIVE_REQUIRES_SLUG_MESSAGE,
                  })
                }
              />
            </Col>
            <Col md={6}>
              <BlogInlineInput parentInputComponent={TextAreaInlineInput} name="remarks" label="Remarks" />
            </Col>
            <Col md={6}>
              <BlogInlineInput
                parentInputComponent={TagsInlineInput}
                name="tags"
                label="Tags"
                help="Tags label and group the blog."
                editContent={({
                  value,
                  setNewValue,
                  saveNow,
                  cancelNow,
                  submitting,
                  canEdit,
                }: InlineInputContentOptions) => (
                  <div>
                    <TagsInput submitting={submitting} value={value} onChange={(e) => setNewValue(e)} />
                    <HorizontalStack gap={2} className="mt-2">
                      <GenerateTagsButton
                        blogId={document?._id ?? ''}
                        onTagsGenerated={(tags) => setNewValue(tags)}
                        disabled={!canEdit || submitting || !document?._id}
                      />
                      <SaveCancelButtons
                        isBusy={submitting}
                        disabled={!canEdit}
                        onSave={saveNow}
                        onCancel={cancelNow}
                      />
                    </HorizontalStack>
                  </div>
                )}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="mt-5">
        {canEdit ? (
          <BlogInlineInput
            displayContent={(value, onClick, { canEdit: canEditContent }) =>
              renderContent(value, onClick, canEditContent)
            }
            hoverToEdit={false}
            name="contentHtml"
            parentInputComponent={RichTextInlineInput}
          />
        ) : (
          // InlineInput wraps the whole block in one "Editing is disabled" popover; render outside it.
          renderContent(document?.contentHtml, undefined, false)
        )}
      </div>
    </DataSuspense>
  )
}
